import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle, Copy, CreditCard, Eye, Loader2, MoreHorizontal, PenSquare, RotateCcw, Search, Shield, Trash2, Users,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useRegion } from '../hooks/useRegion';
import { formatRegionalDate } from '../lib/regionConfig';
import { signInProviderLabel } from '../lib/signInProviders';
import { formatMessageTime } from '../lib/support';
import { planState } from '../lib/planState';
import { Layout } from '../components/Layout';
import { AdminGate, useAdminAccess } from '../components/AdminGate';
import { PageHeader } from '../components/PageHeader';
import { TabCount } from '../components/TabCount';
import { PageToolbar, STICKY_TABLE, stickyTableHeader } from '../components/PageToolbar';
import { PlanBadge } from '../components/admin/PlanBadge';
import { UserSupportDialog } from '../components/admin/UserSupportDialog';
import { SubscriptionOverrideDialog } from '../components/admin/SubscriptionOverrideDialog';
import { NewMessageDialog } from '../components/support/NewMessageDialog';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

// The tabs, and which users each shows
const VIEWS = [
  { value: 'all', label: 'All', test: () => true },
  { value: 'subscribed', label: 'Subscribed', test: (u) => ['paid', 'ending'].includes(planState(u)) },
  { value: 'trial', label: 'Trial', test: (u) => planState(u) === 'trial' },
  { value: 'ended', label: 'Ended', test: (u) => ['expired', 'canceled'].includes(planState(u)) },
  { value: 'admins', label: 'Admins', test: (u) => u.is_admin },
];

/** Where an account came from, for search: "Instagram ad Spring · Reel 2 demo" */
const sourceText = (u) => {
  const s = u.signup_source;
  return s ? `${s.label} ${s.detail || ''} ${s.from_demo ? 'demo' : ''}` : '';
};

/** The label, then the campaign and ad when the link carried them. */
function SignupSource({ source }) {
  if (!source) return <span className="text-sm text-muted-foreground">-</span>;
  const detail = [source.detail, source.from_demo && 'Tried the demo'].filter(Boolean).join(' · ');
  return (
    <span className="block max-w-[11rem] min-w-0" title={detail || undefined}>
      <span className="block truncate text-sm">{source.label}</span>
      {detail && <span className="block truncate text-sm text-muted-foreground">{detail}</span>}
    </span>
  );
}

/**
 * Users, for admins: everyone with an account. Tabs filter by plan, search by
 * name, email or where they came from (signup_source.py names it). Each row opens that user's support dashboard, messages them
 * through Support, or (from its menu) changes their account.
 */
export default function UsersPage() {
  const adminAccess = useAdminAccess();
  const navigate = useNavigate();
  const { region } = useRegion();
  const { user: me, getAdminUsers, deleteUser, resetUserPassword, toggleUserAdmin } = useAuthStore();

  const [users, setUsers] = useState(null);
  const [view, setView] = useState('all');
  const [search, setSearch] = useState('');

  // Each dialog holds the user it is about, or null when closed
  const [viewing, setViewing] = useState(null);
  const [messaging, setMessaging] = useState(null);
  const [overriding, setOverriding] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);

  const loadUsers = useCallback(async () => {
    const result = await getAdminUsers();
    if (result.success) {
      setUsers(result.users);
    } else {
      setUsers((prev) => prev || []);
      toast.error(result.error || 'Failed to load users');
    }
  }, [getAdminUsers]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const counts = useMemo(
    () => Object.fromEntries(VIEWS.map((v) => [v.value, (users || []).filter(v.test).length])),
    [users]
  );

  // Admins first, then the busiest accounts
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    const { test } = VIEWS.find((v) => v.value === view);
    return (users || [])
      .filter(test)
      .filter((u) => !term || `${u.name} ${u.email} ${sourceText(u)}`.toLowerCase().includes(term))
      .sort((a, b) => (b.is_admin - a.is_admin)
        || ((b.job_count || 0) + (b.equipment_count || 0)) - ((a.job_count || 0) + (a.equipment_count || 0)));
  }, [users, view, search]);

  const closeViewing = useCallback((open) => { if (!open) setViewing(null); }, []);

  const startMessage = (u) => {
    setViewing(null);
    setMessaging(u);
  };

  const messageSent = (thread) => {
    toast.success(`Sent to ${thread.customer_name || thread.customer_email}`, {
      action: { label: 'Open', onClick: () => navigate(`/support/${thread.id}`) },
    });
  };

  const handleDelete = async () => {
    const result = await deleteUser(deleting.id);
    setDeleting(null);
    if (result.success) {
      toast.success('User deleted');
      loadUsers();
    } else {
      toast.error(result.error || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (u) => {
    const result = await resetUserPassword(u.id);
    if (result.success) {
      setTempPassword(result);
    } else {
      toast.error(result.error || 'Failed to reset password');
    }
  };

  const handleToggleAdmin = async (u) => {
    const result = await toggleUserAdmin(u.id, !u.is_admin);
    if (result.success) {
      toast.success(u.is_admin ? `${u.email} is no longer an admin` : `${u.email} is now an admin`);
      loadUsers();
    } else {
      toast.error(result.error || 'Failed to update admin status');
    }
  };

  const copyTempPassword = () => {
    navigator.clipboard.writeText(tempPassword.temporary_password);
    toast.success('Password copied');
  };

  if (adminAccess !== 'yes') return <AdminGate access={adminAccess} />;

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader title="Users" />
        {/* No hairline: the sticky table header sits directly under it */}
        <PageToolbar hairline={false}>
          {/* A row that cannot fit scrolls sideways itself (DESIGN.md) */}
          <div className="overflow-x-auto max-w-full min-w-0">
            <Tabs value={view} onValueChange={setView}>
              <TabsList>
                {VIEWS.map((v) => (
                  <TabsTrigger key={v.value} value={v.value} data-testid={`users-view-${v.value}`}>
                    {v.label}
                    {users && <TabCount count={counts[v.value]} />}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          <div className="relative sm:w-64">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users"
              className="pl-10"
              data-testid="users-search"
            />
          </div>
        </PageToolbar>

        {users === null ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : shown.length === 0 ? (
          <div className="rounded-cards bg-surface p-8 text-center text-muted-foreground">
            <Users className="w-8 h-8 text-ink-faded mx-auto mb-3" />
            {search ? `No users match "${search}".` : 'No users here.'}
          </div>
        ) : (
          <Card className={STICKY_TABLE.card}>
            <Table containerClassName={STICKY_TABLE.container} data-testid="users-table">
              <TableHeader {...stickyTableHeader()}>
                <TableRow>
                  <TableHead className="px-4">User</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="hidden min-[1280px]:table-cell">Source</TableHead>
                  <TableHead className="hidden md:table-cell text-right">Jobs</TableHead>
                  <TableHead className="hidden md:table-cell text-right">Equipment</TableHead>
                  <TableHead className="hidden min-[1440px]:table-cell">Sign-in</TableHead>
                  <TableHead className="hidden min-[1280px]:table-cell">Last sign-in</TableHead>
                  <TableHead className="hidden min-[1440px]:table-cell">Signed up</TableHead>
                  <TableHead className="w-px"><span className="sr-only">Actions</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="px-4">
                      <button
                        type="button"
                        onClick={() => setViewing(u)}
                        className="block text-left min-w-0 max-w-[11rem] sm:max-w-[16rem] group"
                        data-testid={`view-support-${u.id}`}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="truncate group-hover:underline underline-offset-4">{u.name || u.email}</span>
                          {u.is_admin && (
                            <span className="text-xs px-2 py-0.5 rounded-pill bg-inverse text-inverse-foreground shrink-0">Admin</span>
                          )}
                        </span>
                        <span className="block truncate text-sm text-muted-foreground">{u.email}</span>
                      </button>
                    </TableCell>
                    <TableCell><PlanBadge user={u} /></TableCell>
                    <TableCell className="hidden min-[1280px]:table-cell"><SignupSource source={u.signup_source} /></TableCell>
                    <TableCell className="hidden md:table-cell text-right tabular-nums">{u.job_count || 0}</TableCell>
                    <TableCell className="hidden md:table-cell text-right tabular-nums">{u.equipment_count || 0}</TableCell>
                    <TableCell className="hidden min-[1440px]:table-cell text-sm text-muted-foreground whitespace-nowrap">
                      {signInProviderLabel(u.auth_type) || 'Email'}
                    </TableCell>
                    <TableCell className="hidden min-[1280px]:table-cell text-sm text-muted-foreground whitespace-nowrap tabular-nums">
                      {formatMessageTime(u.last_login_at, region) || 'Never'}
                    </TableCell>
                    <TableCell className="hidden min-[1440px]:table-cell text-sm text-muted-foreground whitespace-nowrap tabular-nums">
                      {formatRegionalDate(u.created_at, region) || '-'}
                    </TableCell>
                    <TableCell className="pr-4">
                      <div className="flex items-center justify-end gap-1">
                        {u.auth_type !== 'demo' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startMessage(u)}
                            title={`Message ${u.name || u.email}`}
                            data-testid={`message-user-${u.id}`}
                          >
                            <PenSquare />
                            <span className="hidden sm:inline lg:hidden min-[1280px]:inline">Message</span>
                          </Button>
                        )}
                        <UserMenu
                          user={u}
                          isMe={u.id === me?.id}
                          onView={() => setViewing(u)}
                          onOverride={() => setOverriding(u)}
                          onResetPassword={() => handleResetPassword(u)}
                          onToggleAdmin={() => handleToggleAdmin(u)}
                          onDelete={() => setDeleting(u)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <UserSupportDialog user={viewing} onOpenChange={closeViewing} onMessage={startMessage} />

      <NewMessageDialog
        open={!!messaging}
        to={messaging}
        onOpenChange={(open) => { if (!open) setMessaging(null); }}
        onSent={messageSent}
      />

      <SubscriptionOverrideDialog
        user={overriding}
        onOpenChange={(open) => { if (!open) setOverriding(null); }}
        onChanged={loadUsers}
      />

      <Dialog open={!!deleting} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-[18px] h-[18px] text-destructive shrink-0" />
              Delete this account?
            </DialogTitle>
            <DialogDescription>
              <strong>{deleting?.email}</strong> and everything in their account - jobs, equipment, clients,
              crew and settings - will be deleted for good. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-user">Delete account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempPassword} onOpenChange={(open) => { if (!open) setTempPassword(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Password reset</DialogTitle>
            <DialogDescription>{tempPassword?.message}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="temp-password">Temporary password</Label>
            <div className="flex gap-2">
              <Input id="temp-password" value={tempPassword?.temporary_password || ''} readOnly className="font-code" />
              <Button variant="outline" size="icon" onClick={copyTempPassword} title="Copy">
                <Copy />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">They'll be asked to change it when they next sign in.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPassword(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

/** A row's less common actions. You can't demote or delete yourself here. */
function UserMenu({ user, isMe, onView, onOverride, onResetPassword, onToggleAdmin, onDelete }) {
  const hasPassword = !signInProviderLabel(user.auth_type);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" title="More" aria-label={`More for ${user.email}`} data-testid={`user-menu-${user.id}`}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={onView}>
          <Eye className="w-4 h-4 mr-2" /> Support dashboard
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onOverride} data-testid={`override-sub-${user.id}`}>
          <CreditCard className="w-4 h-4 mr-2" /> Override subscription
        </DropdownMenuItem>
        {hasPassword && (
          <DropdownMenuItem onClick={onResetPassword} data-testid={`reset-pwd-${user.id}`}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset password
          </DropdownMenuItem>
        )}
        {!isMe && (
          <>
            <DropdownMenuItem onClick={onToggleAdmin} data-testid={`toggle-admin-${user.id}`}>
              <Shield className="w-4 h-4 mr-2" /> {user.is_admin ? 'Remove admin' : 'Make admin'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive" data-testid={`delete-user-${user.id}`}>
              <Trash2 className="w-4 h-4 mr-2" /> Delete account
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
